/**
 * What every key does, in one place.
 *
 * Three things were describing the controls independently: the flight loop
 * (which reads the keys), the cockpit card (which draws them), and now the
 * tutorial (which tells you about them). Any two of those could disagree and
 * nothing would fail — the card would simply be lying, and the lie would be
 * discovered by a player who could not work out why `X` did nothing.
 *
 * So there is one table and everything reads it. Rebinding is not built, but
 * when it is, this is the thing that changes and the three consumers get it for
 * free.
 *
 * ── Why the keys are lower case ─────────────────────────────────────────────
 *
 * `KeyboardEvent.key` reports the character produced, so holding shift turns
 * `w` into `W`. Everything downcases before matching; the labels are what get
 * shown.
 */
export const BINDINGS = {
    throttleUp: { action: 'throttleUp', keys: ['w'], label: 'W', description: 'throttle up' },
    throttleDown: { action: 'throttleDown', keys: ['s'], label: 'S', description: 'throttle down' },
    strafeLeft: { action: 'strafeLeft', keys: ['a'], label: 'A', description: 'strafe left' },
    strafeRight: { action: 'strafeRight', keys: ['d'], label: 'D', description: 'strafe right' },
    rollLeft: { action: 'rollLeft', keys: ['q'], label: 'Q', description: 'roll left' },
    rollRight: { action: 'rollRight', keys: ['e'], label: 'E', description: 'roll right' },
    up: { action: 'up', keys: [' '], label: 'SPACE', description: 'thrust up' },
    // Shift rather than control: control is chorded into half the shortcuts a
    // browser has, and every one of them fired while trying to descend.
    down: { action: 'down', keys: ['shift'], label: 'SHIFT', description: 'thrust down' },
    thrusterSet: { action: 'thrusterSet', keys: ['t'], label: 'T', description: 'thruster set' },
    warp: { action: 'warp', keys: ['y'], label: 'Y', description: 'warp drive' },
    jump: { action: 'jump', keys: ['j'], label: 'J', description: 'jump module' },
    landingGear: { action: 'landingGear', keys: ['g'], label: 'G', description: 'landing gear' },
    cruise: { action: 'cruise', keys: ['r'], label: 'R', description: 'cruise control' },
    dock: { action: 'dock', keys: ['f'], label: 'F', description: 'dock' },
    view: { action: 'view', keys: ['v'], label: 'V', description: 'recentre / view' },
    nextSeat: { action: 'nextSeat', keys: ['c'], label: 'C', description: 'next seat' },
    lookAround: { action: 'lookAround', keys: [], label: 'MOUSE 3', description: 'look around' },
    cursor: { action: 'cursor', keys: ['alt'], label: 'ALT', description: 'hold for cursor' },
};
/**
 * The order the cockpit card lists them in.
 *
 * Grouped by when you learn them: flying, then the systems you throw once you
 * are somewhere, then the camera.
 */
export const CARD_ORDER = [
    'throttleUp',
    'strafeLeft',
    'rollLeft',
    'up',
    'thrusterSet',
    'warp',
    'jump',
    'landingGear',
    'cruise',
    'dock',
    'view',
    'nextSeat',
    'lookAround',
    'cursor',
];
/** How a key is written when it is spoken about. */
export function keyLabel(action) {
    return BINDINGS[action].label;
}
/**
 * Both halves of a paired control, as one label.
 *
 * The card has no room for two lines saying "throttle up" and "throttle down",
 * and nobody thinks of them as separate controls anyway.
 */
export function pairLabel(a, b) {
    return `${keyLabel(a)}/${keyLabel(b)}`;
}
/** Whether this key event is this action. Case-insensitive, as events are not. */
export function isAction(action, key) {
    return BINDINGS[action].keys.includes(key.toLowerCase());
}
/** What a key does, if anything. */
export function actionFor(key) {
    const pressed = key.toLowerCase();
    for (const binding of Object.values(BINDINGS)) {
        if (binding.keys.includes(pressed))
            return binding.action;
    }
    return null;
}
//# sourceMappingURL=input.js.map